/**
 * Stepper header colour settings — shared helpers (Issue #480).
 *
 * The workflow canvas Settings → STEPPER panel exposes three optional
 * colours on the form-level `styling` object:
 *
 *   - stepper_completed_color   (default: emerald-500 #10b981)
 *   - stepper_active_color      (default: stone-900   #1c1917)
 *   - stepper_upcoming_color    (default: stone-200   #e7e5e4)
 *
 * Each key is an optional `#rgb`/`#rrggbb` hex string; null/absent/invalid
 * means "use the built-in default", so existing forms render pixel-identically.
 *
 * PublicForm renders custom colours as INLINE STYLES (Tailwind classes can't
 * take runtime values) and derives the numeral foreground + active ring from
 * the chosen background so any pick stays legible:
 *
 *   - numeral: white when the background is dark enough (WCAG relative
 *     luminance < 0.45 — emerald-500 ≈ 0.36 stays white like today's look),
 *     else stone-700 (#44403c), which reads on any light pick.
 *   - active ring: the active colour at 10% opacity, mirroring the default
 *     Tailwind ring treatment.
 */

// Accepts 3- or 6-digit hex, e.g. #fff / #10b981. Nothing else.
export const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export function isValidHex(value) {
    return typeof value === 'string' && HEX_COLOR_RE.test(value);
}

/** "#abc" → {r,g,b}; returns null for anything that isn't valid hex. */
export function hexToRgb(hex) {
    if (!isValidHex(hex)) return null;
    let h = hex.slice(1);
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
}

/** WCAG 2.x relative luminance of an {r,g,b} triple (0-255 channels). */
export function relativeLuminance(rgb) {
    if (!rgb) return null;
    const channel = c => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two hex colours (1–21); null if either is invalid. */
export function contrastRatio(hexA, hexB) {
    const la = relativeLuminance(hexToRgb(hexA));
    const lb = relativeLuminance(hexToRgb(hexB));
    if (la == null || lb == null) return null;
    const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
    return (hi + 0.05) / (lo + 0.05);
}

// Backgrounds at or below this luminance get white numerals. 0.45 keeps the
// shipped default look: emerald-500 (#10b981, L≈0.36) → white, while any
// light pick (stone-200 L≈0.79, near-white) gets a dark numeral.
const WHITE_NUMERAL_MAX_LUMINANCE = 0.45;
const DARK_NUMERAL = '#44403c'; // stone-700 — legible on any light background

/** Numeral/icon colour for a dot background; null when hex is invalid. */
export function deriveNumeralColor(hex) {
    const lum = relativeLuminance(hexToRgb(hex));
    if (lum == null) return null;
    return lum < WHITE_NUMERAL_MAX_LUMINANCE ? '#ffffff' : DARK_NUMERAL;
}

/** Active-dot ring shadow: the active colour at 10% opacity. */
export function deriveRingShadow(hex) {
    const rgb = hexToRgb(hex);
    if (!rgb) return null;
    return `0 0 0 4px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;
}

/**
 * Reads the three optional stepper colour keys off a form `styling` object.
 * Invalid values (legacy garbage, hand-edited docs) are treated as unset so
 * the renderer falls back to the built-in default classes.
 */
export function resolveStepperColors(styling) {
    const s = styling && typeof styling === 'object' ? styling : {};
    const pick = key => (isValidHex(s[key]) ? s[key].toLowerCase() : null);
    return {
        completed: pick('stepper_completed_color'),
        active: pick('stepper_active_color'),
        upcoming: pick('stepper_upcoming_color'),
    };
}
